import * as ko from "knockout";
import PromiseRequest from "../../../../req/PromiseRequest";
import {
    IAuthenticationType,
    IFormTemplate,
    IMessageData,
    IRouteDefinitionParams,
    IRuleset,
    ISyncField
} from '../../../../index';
import {KOSubscriptionTracker} from "../../../../util/KOSubscriptionTracker";
import {ObjectTypeId} from "../../../../constants";
import {global} from "../../../../appContext";
import {PageData} from "./PageData";
import {ErrorTracker} from "../../../../error.tracker";
import {_} from 'lodash';
import {ICustomButton} from "../../../heading/AbstractHeading";
import {nsSyncFields} from "../../../../req/nsSyncFields";
import {nsRoute} from "../../../../util/route";
import {REQUEST_MESSAGE} from "../../../../constants/Language";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import detailsHtml from './details.html?raw';

export let template = detailsHtml;

export abstract class viewModel extends ParsedRouteDataDecorator {
    protected allAuthenticationTypes!: IAuthenticationType[];
    protected data!: IFormTemplate;
    protected readonly ObjectTypeId = ObjectTypeId;
    protected readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    protected readonly kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected readonly reqGetAuthenticationTypes: PromiseRequest = global.getPromiseRequest('getauthenticationtypes');
    protected readonly et: ErrorTracker = global.errorAreas.get('form.template.mutable');
    protected readonly heading: KnockoutObservable<string | null> = ko.observable(null);
    protected readonly abstract reqSubmit: PromiseRequest;

    protected readonly name: KnockoutObservable<string | null> = ko.observable(null);
    protected readonly active: KnockoutObservable<boolean | null> = ko.observable(null);
    protected readonly published: KnockoutObservable<boolean | null> = ko.observable(null);
    protected readonly authenticationTypes: KnockoutObservableArray<IAuthenticationType> = ko.observableArray<IAuthenticationType>([]);
    protected readonly availableTo: KnockoutObservable<IRuleset | undefined> = ko.observable<IRuleset | undefined>(undefined);
    protected readonly pages: KnockoutObservableArray<PageData> = ko.observableArray<PageData>([]);

    protected readonly customButtons: KnockoutObservableArray<ICustomButton> = ko.observableArray<ICustomButton>([]);
    protected syncfields!: ISyncField[];
    
    constructor(params: IRouteDefinitionParams) {
        super(params);
    }

    protected init(): Promise<any> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});
        return Promise.resolve()
            .then(() => {
                return this.reqGetAuthenticationTypes
                    .request()
                    .then((data: IAuthenticationType[]) => {
                        this.allAuthenticationTypes = _.sortBy(data, (a: IAuthenticationType) => a.name.toLowerCase());
                    });
            })
            .then(() => {
                const obj = nsSyncFields.getSyncFields(ObjectTypeId.FormData);
                return obj.request
                    .then((data: ISyncField[]) => {
                        this.syncfields = data;
                    });
            })
    }

    /*******************************************************************************************************************
     * View interaction
     ******************************************************************************************************************/

    protected onDiscardClick() {
        this.init();
        global.snackbarData({message: `All changes discarded`});
    }

    protected onAddPageClick() {
        this.pages.push(new PageData());
    }

    public onMoveUpClick(page: PageData) {
        const index = this.pages.indexOf(page);
        this.pages.remove(page);
        this.pages.splice(index - 1, 0, page);
    }

    public onMoveDownClick(page: PageData) {
        const index = this.pages.indexOf(page);
        this.pages.remove(page);
        this.pages.splice(index + 1, 0, page);
    }

    public onDeleteClick(page: PageData) {
        const index = this.pages().indexOf(page);
        this.pages.remove(page);
        global.snackbarData({
            message: 'Deleted s|s:((page|ac))', undo: () => {
                this.pages.splice(index, 0, page)
            }
        });
    }

    /*******************************************************************************************************************
     * Helpers / computed properties
     ******************************************************************************************************************/

    protected isFirst(page: PageData) {
        return this.pages.indexOf(page) == 0;
    }

    protected isLast(page: PageData) {
        return this.pages.indexOf(page) == this.pages().length - 1;
    }

    protected isValid(): boolean {
        const a = !!this.name();
        this.et.update(!a, this.name, 'Invalid name');
        const b = !!this.authenticationTypes().length;
        this.et.update(!b, this.authenticationTypes, 'Invalid authentication type');
        const c = !!this.pages().find(p => !!p.hasInputField());
        const duplicateIdentifiers: string[] = _(this.pages())
            .map((p: PageData) => p.getIdentifiers())
            .flatten()
            .groupBy()
            .filter((o: string[]) => o.length >= 2)
            .flatten()
            .uniq()
            .value();
        const d = this.pages().reduce((bool: boolean, p: PageData, i: number) => {
            return p.isValid(c, duplicateIdentifiers) && bool;
        }, true);
        return a && b && c && d;
    }

    protected isChanged(): boolean {
        return !_.isEqual(this.getSubmitData(), this.data);
    }

    /*******************************************************************************************************************
     * Submit
     ******************************************************************************************************************/

    protected abstract onSubmit(): void;

    protected getSubmitData(): IFormTemplate {
        return _.defaults({
            active: this.active(),
            published: this.published(),
            availableto_id: this.availableTo()?.id ?? 0,
            authenticationtypes: this.authenticationTypes().map(a => a.flag).reduce((sum: number, val: number) => sum + val, 0),
            name: this.name(),
            pages: this.pages().map((p: PageData, index: number) => {
                const ret = p.getSubmitData();
                ret.order = index + 1;
                return ret;
            }),
        }, this.data);
    }

    /*******************************************************************************************************************
     * Miscellaneous
     ******************************************************************************************************************/

    override dispose() {
        this.kosub.unsuscribeAll();
        this.reqGetAuthenticationTypes.abort();
        this.reqSubmit.abort();
    }
}
